if (typeof customElements.get('youtube-videos') === 'undefined') {
	class YouTubeVideos extends HTMLElement {
		constructor() {
			super();

			this.uniqueClass = `.splide--${this.dataset.id}`;
			this.content = this.querySelector('.youtube-videos__content');
			this.grid = this.querySelector('.youtube-videos__grid');
			this.loading = this.querySelector('.youtube-videos__loading');
			this.error = this.querySelector('.youtube-videos__error');
			this.loadMoreContainer = this.querySelector('.youtube-videos__load-more');
			this.loadMoreBtn = this.querySelector('#load-more-btn-' + this.dataset.id);
			this.mobileBreakpoint = 640;
			this.isMobile = window.innerWidth <= this.mobileBreakpoint;
			this.splide = null;
			this.videos = [];
			this.modal = null;
			this.allVideos = [];
			this.currentPage = 0;
			this.videosPerPage = parseInt(this.getAttribute('data-loaded-videos')) || 6;
			this.hasMoreVideos = true;

			this.init();
		}

		init() {
			this.loadVideos();
			this.handleResize = this.handleResize.bind(this);
			this.handleLoadMore = this.handleLoadMore.bind(this);
			window.addEventListener('resize', this.handleResize);

			if (this.loadMoreBtn) {
				this.loadMoreBtn.addEventListener('click', this.handleLoadMore);
			}

			if (window.Shopify.designMode) {
				document.addEventListener('shopify:section:load', () => {
					this.destroyCarousel();
					this.init();
				});
			}
		}

		async loadVideos() {
			try {
				const channelId = 'UCWH7ZQJVJFo_1MMp2wZRI5g';
				const enableCarousel = this.getAttribute('data-enable-carousel') === 'true';
				const loadedVideos = parseInt(this.getAttribute('data-loaded-videos')) || 9;
				const maxResults = enableCarousel ? 6 : loadedVideos;
				const filterKeyword = this.getAttribute('data-filter-keyword') || '';
				const apiKey = 'AIzaSyA41vokqRXlZB5lVk-0_ITLEFpdOCHXwd4';

				// Get channel uploads playlist
				const channelResponse = await fetch(
					`https://www.googleapis.com/youtube/v3/channels?part=contentDetails&id=${channelId}&key=${apiKey}`
				);

				if (!channelResponse.ok) {
					throw new Error('Failed to fetch channel data');
				}

				const channelData = await channelResponse.json();

				if (!channelData.items || channelData.items.length === 0) {
					throw new Error('Channel not found');
				}

				const uploadsPlaylistId = channelData.items[0].contentDetails.relatedPlaylists.uploads;

				// Get videos from uploads playlist (fetch more for pagination)
				const videosResponse = await fetch(
					`https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&playlistId=${uploadsPlaylistId}&maxResults=50&key=${apiKey}`
				);

				if (!videosResponse.ok) {
					throw new Error('Failed to fetch videos data');
				}

				const videosData = await videosResponse.json();

				if (!videosData.items || videosData.items.length === 0) {
					throw new Error('No videos found');
				}

				// Filter videos by keyword if specified
				let filteredVideos = videosData.items;
				if (filterKeyword) {
					const keyword = filterKeyword.toLowerCase();
					filteredVideos = videosData.items.filter((item) =>
						item.snippet.title.toLowerCase().includes(keyword)
					);
				}

				if (filteredVideos.length === 0) {
					throw new Error(`No videos found matching keyword: ${filterKeyword}`);
				}

				// Get video details for all filtered videos
				const videoIds = filteredVideos.map((item) => item.contentDetails.videoId).join(',');
				const videoDetailsResponse = await fetch(
					`https://www.googleapis.com/youtube/v3/videos?part=contentDetails,statistics&id=${videoIds}&key=${apiKey}`
				);

				if (!videoDetailsResponse.ok) {
					throw new Error('Failed to fetch video details');
				}

				const videoDetailsData = await videoDetailsResponse.json();

				// Store all videos
				this.allVideos = filteredVideos.map((item, index) => {
					const videoDetails = videoDetailsData.items[index];
					const duration = videoDetails?.contentDetails?.duration || 'PT0S';
					const viewCount = parseInt(videoDetails?.statistics?.viewCount) || 0;

					return {
						id: item.contentDetails.videoId,
						title: item.snippet.title,
						thumbnail:
							item.snippet.thumbnails.high?.url ||
							item.snippet.thumbnails.medium?.url ||
							item.snippet.thumbnails.default?.url,
						publishedAt: new Date(item.snippet.publishedAt),
						duration: this.parseDuration(duration),
						viewCount: viewCount,
						url: `https://www.youtube.com/watch?v=${item.contentDetails.videoId}`,
						embedUrl: `https://www.youtube.com/embed/${item.contentDetails.videoId}`,
						description: item.snippet.description,
					};
				});

				// Set initial videos
				this.videos = this.allVideos.slice(0, this.videosPerPage);
				this.currentPage = 1;
				this.hasMoreVideos = this.allVideos.length > this.videosPerPage;

				this.renderVideos();
			} catch (error) {
				console.error('Error loading YouTube videos:', error);
				this.showError();
			}
		}

		async handleLoadMore() {
			if (!this.hasMoreVideos || !this.loadMoreBtn) return;

			const loadingText = this.loadMoreBtn.getAttribute('data-loading-text');
			const originalText = this.loadMoreBtn.textContent;

			// Show loading state
			this.loadMoreBtn.textContent = loadingText;
			this.loadMoreBtn.disabled = true;

			try {
				// Simulate loading delay for better UX
				await new Promise((resolve) => setTimeout(resolve, 500));

				// Calculate next batch of videos
				const startIndex = this.currentPage * this.videosPerPage;
				const endIndex = startIndex + this.videosPerPage;
				const newVideos = this.allVideos.slice(startIndex, endIndex);

				// Add new videos to current videos
				this.videos = [...this.videos, ...newVideos];
				this.currentPage++;

				// Check if there are more videos
				this.hasMoreVideos = endIndex < this.allVideos.length;

				// Re-render videos
				this.renderVideos();

				// Hide load more button if no more videos
				if (!this.hasMoreVideos && this.loadMoreContainer) {
					this.loadMoreContainer.style.display = 'none';
				}
			} catch (error) {
				console.error('Error loading more videos:', error);
			} finally {
				// Restore button state
				this.loadMoreBtn.textContent = originalText;
				this.loadMoreBtn.disabled = false;
			}
		}

		parseDuration(duration) {
			const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);

			if (!match) return '0:00';

			const hours = parseInt(match[1]) || 0;
			const minutes = parseInt(match[2]) || 0;
			const seconds = parseInt(match[3]) || 0;

			if (hours > 0) {
				return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
			}
			return `${minutes}:${seconds.toString().padStart(2, '0')}`;
		}

		formatDate(date) {
			const now = new Date();
			const diffTime = Math.abs(now - date);
			const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

			if (diffDays === 1) return 'acum o zi';
			if (diffDays < 7) return `acum ${diffDays} zile`;
			if (diffDays < 30) {
				const weeks = Math.floor(diffDays / 7);
				return weeks === 1 ? 'acum o săptămână' : `acum ${weeks} săptămâni`;
			}
			if (diffDays < 365) {
				const months = Math.floor(diffDays / 30);
				return months === 1 ? 'acum o lună' : `acum ${months} luni`;
			}
			const years = Math.floor(diffDays / 365);
			return years === 1 ? 'acum un an' : `acum ${years} ani`;
		}

		formatViewCount(count) {
			if (count >= 1000000) {
				return `${(count / 1000000).toFixed(1)}M vizionări`;
			}
			if (count >= 1000) {
				return `${(count / 1000).toFixed(1)}K vizionări`;
			}
			return `${count} vizionări`;
		}

		renderVideos() {
			this.hideLoading();

			if (this.videos.length === 0) {
				this.showError();
				return;
			}

			this.grid.innerHTML = '';

			const enableCarousel = this.getAttribute('data-enable-carousel') === 'true';

			if (enableCarousel) {
				this.setupCarousel();
			} else {
				this.setupGrid();
			}

			// Show/hide load more button
			if (this.loadMoreContainer) {
				this.loadMoreContainer.style.display = !enableCarousel && this.hasMoreVideos ? 'block' : 'none';
			}
		}

		setupGrid() {
			this.grid.style.display = 'grid';
			this.grid.className = 'youtube-videos__grid youtube-videos__grid--desktop';

			this.videos.forEach((video) => {
				const videoCard = this.createVideoCard(video);
				this.grid.appendChild(videoCard);
			});
		}

		setupCarousel() {
			// Create splide container
			const splideContainer = document.createElement('div');
			splideContainer.className = `splide splide--${this.dataset.id}`;

			// Create splide track
			const splideTrack = document.createElement('div');
			splideTrack.className = 'splide__track';

			// Create splide list
			const splideList = document.createElement('ul');
			splideList.className = 'splide__list';

			// Create video cards and add to splide slides
			this.videos.forEach((video) => {
				const slide = document.createElement('li');
				slide.className = 'splide__slide';

				const videoCard = this.createVideoCard(video);
				slide.appendChild(videoCard);
				splideList.appendChild(slide);
			});

			// Assemble splide structure
			splideTrack.appendChild(splideList);
			splideContainer.appendChild(splideTrack);

			// Clear grid and add splide
			this.grid.innerHTML = '';
			this.grid.appendChild(splideContainer);
			this.grid.style.display = 'block';

			// Initialize splide
			this.initSplide();
		}

		createVideoCard(video) {
			const card = document.createElement('div');
			card.className = 'youtube-video-card';

			const showDuration = this.getAttribute('data-show-duration') === 'true';
			const showDate = this.getAttribute('data-show-date') === 'true';
			const showViews = this.getAttribute('data-show-views') === 'true';
			const playInModal = this.getAttribute('data-play-in-modal') === 'modal';

			const clickHandler = playInModal
				? `onclick="event.preventDefault(); this.closest('youtube-videos').playVideoInModal('${video.id}', '${video.title}')"`
				: '';

			card.innerHTML = `
				<a href="${video.url}" target="_blank" rel="noopener" class="youtube-video-card__link" ${clickHandler}>
					<div class="youtube-video-card__thumbnail">
						<img 
							src="${video.thumbnail}" 
							alt="${video.title}"
							loading="lazy"
							class="youtube-video-card__image"
						>
						${showDuration ? `<span class="youtube-video-card__duration">${video.duration}</span>` : ''}
						<div class="youtube-video-card__play-button">
							<svg width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.64 3.26-5.42 6.19C.13 13.21 0 24 0 24s.13 10.79 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.87 34.79 68 24 68 24s-.13-10.79-1.48-16.26z" fill="#f00"/>
								<path d="M27 34V14l20.5 10L27 34z" fill="#fff"/>
							</svg>
						</div>
					</div>
					<div class="youtube-video-card__content">
						<h4 class="youtube-video-card__title">${video.title}</h4>
						<div class="youtube-video-card__meta">
							${showDate ? `<span class="youtube-video-card__date">${this.formatDate(video.publishedAt)}</span>` : ''}
							${showViews ? `<span class="youtube-video-card__views">${this.formatViewCount(video.viewCount)}</span>` : ''}
						</div>
					</div>
				</a>
			`;

			return card;
		}

		playVideoInModal(videoId, title) {
			if (this.modal) {
				this.modal.remove();
			}

			this.modal = document.createElement('div');
			this.modal.className = 'youtube-modal';
			this.modal.innerHTML = `
				<div class="youtube-modal__overlay">
					<div class="youtube-modal__content">
						<div class="youtube-modal__header">
							<h3 class="youtube-modal__title">${title}</h3>
							<button class="youtube-modal__close" onclick="this.closest('.youtube-modal').remove()">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</button>
						</div>
						<div class="youtube-modal__video">
							<iframe 
								src="https://www.youtube.com/embed/${videoId}?autoplay=1" 
								frameborder="0" 
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
								allowfullscreen
							></iframe>
						</div>
					</div>
				</div>
			`;

			document.body.appendChild(this.modal);

			// Close modal on escape key
			const handleEscape = (e) => {
				if (e.key === 'Escape') {
					this.modal.remove();
					document.removeEventListener('keydown', handleEscape);
				}
			};
			document.addEventListener('keydown', handleEscape);

			// Close modal on overlay click
			this.modal.querySelector('.youtube-modal__overlay').addEventListener('click', (e) => {
				if (e.target === e.currentTarget) {
					this.modal.remove();
				}
			});
		}

		initSplide() {
			this.splide = new Splide(this.uniqueClass, {
				type: 'slide',
				gap: '20px',
				perPage: this.isMobile ? 1 : 3,
				perMove: 1,
				arrows: true,
				pagination: true,
				drag: true,
				rewind: true,
				lazyLoad: 'nearby',
				breakpoints: {
					640: {
						perPage: 1,
						gap: '15px',
					},
					480: {
						gap: '10px',
					},
				},
			});

			this.splide.mount();
		}

		handleResize() {
			const wasMobile = this.isMobile;
			this.isMobile = window.innerWidth <= this.mobileBreakpoint;

			if (wasMobile !== this.isMobile) {
				this.destroyCarousel();
				this.renderVideos();
			}
		}

		destroyCarousel() {
			if (this.splide) {
				this.splide.destroy();
				this.splide = null;
			}
		}

		hideLoading() {
			if (this.loading) {
				this.loading.style.display = 'none';
			}
		}

		showError() {
			this.hideLoading();
			if (this.error) {
				this.error.style.display = 'block';
			}
		}
	}

	customElements.define('youtube-videos', YouTubeVideos);
}
